/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import {
	type RovoExperienceSidebarAPI,
	useRovoExperienceAPIStore,
} from '@atlassian/rovo-experience-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIButton, AIButtonRefreshed } from '../../../ai-button';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIProactiveNudge, AIProactiveNudgeRefreshed } from '../../../ai-proactive-nudge';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isBrowser } from '../../../common/utils/is-browser';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
});

type AISmartButtonUIProps = {
	isDisabled: boolean;
	isRefreshed?: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	product?: string;
	sidebarApi?: RovoExperienceSidebarAPI;
};

export const AISmartButtonUI = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			sidebarApi,
			isRefreshed,
			product,
		}: AISmartButtonUIProps,
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

		const [showButtonAnimation, setShowButtonAnimation] = useState(false);
		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const placementElementRef = useRef<HTMLDivElement | null>(null);

		const onboardingSpotlightExperimentCohort = useMemo(
			() =>
				product === 'confluence'
					? FeatureGates.getExperimentValue<string>(
							'confluence_rovo_button_onboarding_spotlight',
							'cohort',
							'not-enrolled',
							{ fireExperimentExposure: false },
						)
					: 'not-enrolled',
			[product],
		);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (
				!isBrowser() ||
				typeof document === 'undefined' ||
				!isRefreshed ||
				!fg('rovo_button_onboarding_spotlight') ||
				onboardingSpotlightExperimentCohort === 'not-enrolled'
			) {
				return;
			}

			if (onboardingSpotlightExperimentCohort === 'control') {
				FeatureGates.manuallyLogExperimentExposure('confluence_rovo_button_onboarding_spotlight');
				return;
			}

			if (!placementElementRef.current) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === 'rovo-button-onboarding-spotlight') {
					setSpotlightRendered(true);
					// Log experiment exposure for non-control users after spotlight is rendered
					FeatureGates.manuallyLogExperimentExposure('confluence_rovo_button_onboarding_spotlight');
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === 'rovo-button-onboarding-spotlight') {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(document, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [isRefreshed, handleGetStartedClick, onboardingSpotlightExperimentCohort]);

		useEffect(() => {
			if (!isRefreshed || !fg('rovo_button_onboarding_spotlight') || spotlightRendered) {
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				if (!spotlightRendered && !canShowNudge) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [isRefreshed, spotlightRendered, canShowNudge]);

		const nudgeOrPlacement = useMemo(() => {
			if (!isRefreshed || !sidebarApi) {
				return <AIProactiveNudge />;
			}

			const qualifiesForSpotlight =
				onboardingSpotlightExperimentCohort !== 'control' &&
				onboardingSpotlightExperimentCohort !== 'not-enrolled';

			return (
				<React.Fragment>
					{
						// eslint-disable-next-line @atlaskit/platform/no-preconditioning
						fg('rovo_button_onboarding_spotlight') && qualifiesForSpotlight && (
							<div id="rovo-button-onboarding-spotlight" ref={placementElementRef} />
						)
					}
					{canShowNudge && (
						<AIProactiveNudgeRefreshed
							sidebarApi={sidebarApi}
							product={product}
							setShowButtonAnimation={setShowButtonAnimation}
						/>
					)}
				</React.Fragment>
			);
		}, [
			isRefreshed,
			sidebarApi,
			product,
			setShowButtonAnimation,
			canShowNudge,
			onboardingSpotlightExperimentCohort,
		]);

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				<Tooltip
					position="left"
					content={formatMessage(i18n.tooltipLabel)}
					hideTooltipOnClick
					ignoreTooltipPointerEvents
				>
					{isRefreshed ? (
						<AIButtonRefreshed
							onClick={onClick}
							isDisabled={isDisabled}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							ref={ref}
							showAnimation={showButtonAnimation}
						/>
					) : (
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							ref={ref}
						/>
					)}
				</Tooltip>
			</Inline>
		);
	},
);
